import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { apiClient } from '@lib/httpClient/apiClient';
import * as Sentry from '@sentry/react';
import SessionService from '@services/sessions/SessionService';
import BackendClient from '@services/utils/BackendClient';
import HttpClient from '@services/utils/HttpClient';
import WebClient from '@services/utils/WebClient';

import { AuthContext } from './context';
import type { IAuthorization, IUser } from './IUseAuth';

type AuthData = {
  authorization: IAuthorization;
  user: IUser | null;
};

const StorageKeyAuthorization = `GPMED::auth_authorization_1743503299605`;
const StorageKeyCredentials = `GPMED::auth_credentials_1743503299605`;

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [data, setData] = useState<AuthData>(() => {
    const storageToken = localStorage.getItem(StorageKeyAuthorization);
    const storedAuthCredentials = localStorage.getItem(StorageKeyCredentials);
    let token: string | null = null;
    let storedAuthCredentialsParsed: IUser | null = null;
    if (storageToken) {
      const storedTokenParsed: IAuthorization = JSON.parse(storageToken);
      HttpClient.setBearerToken(storedTokenParsed.token!);
      BackendClient.setBearerToken(storedTokenParsed.token!);
      WebClient.setBearerToken(storedTokenParsed.token!);
      apiClient.setBearerToken(storedTokenParsed.token!);
      token = storedTokenParsed.token;
    }
    if (storedAuthCredentials) {
      storedAuthCredentialsParsed = JSON.parse(storedAuthCredentials);
    }
    return {
      authorization: { token },
      user: storedAuthCredentialsParsed,
    };
  });

  const handleLogout = useCallback(() => {
    localStorage.removeItem(StorageKeyAuthorization);
    localStorage.removeItem(StorageKeyCredentials);
    HttpClient.removeBearerToken();
    BackendClient.removeBearerToken();
    WebClient.removeBearerToken();
    setData({
      user: null,
      authorization: { token: null },
    });
  }, []);

  const handleSetUser = useCallback(
    ({
      accessGranted,
      accessGrantedDate,
      country,
      createdAt,
      document,
      email,
      gender,
      gpchatBalance,
      gpchatBetaGranted,
      gpchatGranted,
      gpchatTokens,
      id,
      name,
      occupationArea,
      occupationCity,
      occupationState,
      phone,
      picture,
      plan,
      yearGraduated,
    }: IUser) => {
      setData((oldState) => ({
        ...oldState,
        user: {
          accessGranted,
          accessGrantedDate,
          email,
          gender,
          gpchatBalance,
          gpchatBetaGranted,
          gpchatGranted,
          gpchatTokens,
          id,
          name,
          occupationArea,
          occupationCity,
          occupationState,
          phone,
          picture,
          plan,
          yearGraduated,
          createdAt,
          document,
          country,
        },
      }));
      localStorage.setItem(
        StorageKeyCredentials,
        JSON.stringify({
          accessGranted,
          accessGrantedDate,
          email,
          gender,
          gpchatBalance,
          gpchatBetaGranted,
          gpchatGranted,
          gpchatTokens,
          id,
          name,
          occupationArea,
          occupationCity,
          occupationState,
          phone,
          picture,
          plan,
          yearGraduated,
          createdAt,
          document,
          country,
        }),
      );
    },
    [],
  );

  const handleSetAuthorization = useCallback(({ token }: { token: string }) => {
    localStorage.setItem(StorageKeyAuthorization, JSON.stringify({ token }));
    setData((oldState) => ({
      ...oldState,
      authorization: {
        token,
      },
    }));
  }, []);

  const handleSessionInfo = useCallback(async () => {
    const userInfo = await SessionService.getInfo();
    Sentry.setUser({
      id: userInfo.id,
      email: userInfo.email,
      username: userInfo.name,
    });
    handleSetUser(userInfo);
  }, [handleSetUser]);

  useEffect(() => {
    if (data.authorization.token) {
      handleSessionInfo();
    }
  }, [data.authorization.token, handleSessionInfo]);

  return (
    <AuthContext.Provider
      value={{
        authorization: data.authorization,
        onLogout: handleLogout,
        onSetAuthorization: handleSetAuthorization,
        onSetUser: handleSetUser,
        user: data.user,
        onSessionInfo: handleSessionInfo,
      }}>
      {children}
    </AuthContext.Provider>
  );
};
